import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Modal from "./Modal";
import Button from "./Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import i18next from "i18next";

const formatWysiwygHtml = (html) => {
    if (!html || typeof html !== "string") return "";
    return html.replace(/<a\b([^>]*)>/gi, (match, attrs) => {
        let updatedAttrs = attrs;
        if (/target\s*=/i.test(updatedAttrs)) {
            updatedAttrs = updatedAttrs.replace(
                /target=(["'])[^"']*\1/i,
                'target="_blank"',
            );
        } else {
            updatedAttrs += ' target="_blank"';
        }
        if (/rel\s*=/i.test(updatedAttrs)) {
            if (!/noopener/i.test(updatedAttrs)) {
                updatedAttrs = updatedAttrs.replace(
                    /rel=(["'])([^"']*)\1/i,
                    'rel="$2 noopener noreferrer"',
                );
            }
        } else {
            updatedAttrs += ' rel="noopener noreferrer"';
        }
        return `<a${updatedAttrs}>`;
    });
};

const ModalIntroMedia = ({
    isOpen,
    mediaUrl,
    mediaType,
    title,
    buttonText,
    onContinue,
}) => {
    const videoRef = useRef(null);
    const progressTrackRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const handleTextClick = (e) => {
        const link = e.target.closest("a");
        if (link && link.getAttribute("href")) {
            link.setAttribute("target", "_blank");
            link.setAttribute("rel", "noopener noreferrer");
        }
    };

    const src =
        typeof mediaUrl === "object" && mediaUrl !== null
            ? mediaUrl.url
            : mediaUrl;

    const determineIsVideo = () => {
        if (!src || typeof src !== "string") return false;
        const lower = src.toLowerCase();

        if (
            mediaType === "image" ||
            mediaType === "photo" ||
            lower.endsWith(".jpg") ||
            lower.endsWith(".jpeg") ||
            lower.endsWith(".png") ||
            lower.endsWith(".gif") ||
            lower.endsWith(".webp") ||
            lower.endsWith(".svg")
        ) {
            return false;
        }

        if (
            mediaType === "video" ||
            mediaType === "audio" ||
            mediaType === "media_file" ||
            lower.endsWith(".mp4") ||
            lower.endsWith(".webm") ||
            lower.endsWith(".mov") ||
            lower.endsWith(".ogg") ||
            lower.endsWith(".mp3") ||
            lower.endsWith(".wav") ||
            lower.endsWith(".m4a") ||
            lower.endsWith(".aac") ||
            lower.includes("video") ||
            lower.includes("audio")
        ) {
            return true;
        }

        return false;
    };

    const determineIsText = () => {
        if (mediaType === "text") return true;
        if (!src || typeof src !== "string") return false;
        if (determineIsVideo()) return false;
        const lower = src.toLowerCase();
        if (
            mediaType === "image" ||
            mediaType === "photo" ||
            lower.endsWith(".jpg") ||
            lower.endsWith(".jpeg") ||
            lower.endsWith(".png") ||
            lower.endsWith(".gif") ||
            lower.endsWith(".webp") ||
            lower.endsWith(".svg")
        ) {
            return false;
        }
        // If it starts with HTML or doesn't look like a URL file, it's text
        return src.trim().startsWith("<") || !src.includes("://");
    };

    const isText = determineIsText();
    const isVideo = !isText && determineIsVideo();

    const handleClose = () => {
        if (videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
        }
        onContinue();
    };

    // Auto play when modal opens with delay, and pause immediately when modal is closed
    useEffect(() => {
        let playTimer;

        if (isOpen && isVideo && videoRef.current) {
            setCurrentTime(0);
            videoRef.current.currentTime = 0;
            videoRef.current.pause();
            setIsPlaying(false);

            playTimer = setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current
                        .play()
                        .then(() => setIsPlaying(true))
                        .catch(() => {
                            setIsPlaying(false);
                        });
                }
            }, 600);
        } else if (!isOpen && videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
        }

        return () => {
            if (playTimer) {
                clearTimeout(playTimer);
            }
            if (videoRef.current) {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        };
    }, [isOpen, isVideo, src]);

    const togglePlay = () => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            videoRef.current
                .play()
                .then(() => setIsPlaying(true))
                .catch(() => {});
        } else {
            videoRef.current.pause();
            setIsPlaying(false);
        }
    };

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;
        setCurrentTime(videoRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
        if (!videoRef.current) return;
        setDuration(videoRef.current.duration || 0);
    };

    const handleSeek = (e) => {
        if (!videoRef.current || !progressTrackRef.current || !duration) return;
        const rect = progressTrackRef.current.getBoundingClientRect();
        const clickX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const seekTime = (clickX / rect.width) * duration;
        videoRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
    };

    const formatTime = (seconds) => {
        if (isNaN(seconds) || seconds < 0) return "00:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    };

    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            isWithCloseButton={false}
            closeOnOutsideClick={false}
            className={`modal-intro-media ${isText ? "is-text-mode" : ""}`}
        >
            <div className="modal-intro-media-wrapper">
                <div
                    className={`modal-intro-media-content ${
                        isText ? "is-text" : ""
                    }`}
                >
                    <button
                        type="button"
                        className="modal-intro-media-close"
                        onClick={handleClose}
                        title={i18next.t("Close") || "Close"}
                    >
                        <span className="close-text">
                            {i18next.t("Close") || "Close"}
                        </span>
                        <FontAwesomeIcon
                            icon={["far", "times"]}
                            className="close-icon"
                        />
                    </button>

                    {isText ? (
                        <div className="modal-intro-media-text-container">
                            {title && (
                                <div className="modal-intro-media-text-header">
                                    <h4 className="modal-intro-media-text-category">
                                        {title}
                                    </h4>
                                </div>
                            )}
                            <div
                                className="modal-intro-media-text-body"
                                onClick={handleTextClick}
                                dangerouslySetInnerHTML={{
                                    __html: formatWysiwygHtml(src),
                                }}
                            />
                        </div>
                    ) : isVideo ? (
                        <div className="modal-intro-media-video-container">
                            <video
                                ref={videoRef}
                                src={src}
                                playsInline
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={handleLoadedMetadata}
                                onPlay={() => setIsPlaying(true)}
                                onPause={() => setIsPlaying(false)}
                                onEnded={handleClose}
                                onClick={togglePlay}
                                className="modal-intro-media-video"
                            />

                            {/* Figma Custom Media Player Controls Bar */}
                            <div className="modal-intro-media__controls">
                                <button
                                    type="button"
                                    className="modal-intro-media__play-btn"
                                    onClick={togglePlay}
                                    aria-label={isPlaying ? "Pause" : "Play"}
                                >
                                    {isPlaying ? (
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="20"
                                            height="20"
                                            fill="#FFFFFF"
                                        >
                                            <rect
                                                x="5"
                                                y="4"
                                                width="4"
                                                height="16"
                                                rx="1"
                                            />
                                            <rect
                                                x="15"
                                                y="4"
                                                width="4"
                                                height="16"
                                                rx="1"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="20"
                                            height="20"
                                            fill="#FFFFFF"
                                        >
                                            <path d="M7 4.5v15a1 1 0 001.55.83l12-7.5a1 1 0 000-1.66l-12-7.5A1 1 0 007 4.5z" />
                                        </svg>
                                    )}
                                </button>

                                <div
                                    ref={progressTrackRef}
                                    className="modal-intro-media__progress-track"
                                    onClick={handleSeek}
                                >
                                    <div
                                        className="modal-intro-media__progress-fill"
                                        style={{ width: `${progressPercent}%` }}
                                    />
                                </div>

                                <span className="modal-intro-media__time">
                                    {formatTime(currentTime)}
                                    &nbsp;&nbsp;/&nbsp;&nbsp;
                                    {formatTime(duration)}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <img
                            src={src}
                            alt="Intro Media"
                            className="modal-intro-media-image"
                        />
                    )}
                </div>

                <div className="modal-intro-media-footer">
                    <Button
                        onClick={handleClose}
                        background="linear-gradient(0deg, #FDD100 0%, #FFE033 100%)"
                        color="#10069F"
                        style={{
                            fontWeight: "600",
                            padding: "12px 32px",
                            borderRadius: "0",
                        }}
                    >
                        {buttonText ||
                            i18next.t("Continue to Question") ||
                            "Lanjutkan ke Pertanyaan"}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

ModalIntroMedia.propTypes = {
    isOpen: PropTypes.bool,
    mediaUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    mediaType: PropTypes.string,
    title: PropTypes.string,
    buttonText: PropTypes.string,
    onContinue: PropTypes.func.isRequired,
};

ModalIntroMedia.defaultProps = {
    isOpen: false,
    mediaUrl: "",
    mediaType: "video",
    title: "",
    buttonText: "",
};

export default ModalIntroMedia;
